import {
  getLeoUuid,
  LeoUUID,
  getInt32,
  getObject,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { TypesSortOrder } from "../types/typesSortOrder";
import { EmailCycle, EmailCycleDTO } from "./emailCycle";
/* eslint-disable import/export */
export abstract class GetEmailCyclesRPC implements
  LeoRPC<GetEmailCyclesRPC.Request, GetEmailCyclesRPC.Response, GetEmailCyclesRPC.Errors.Errors>
{
  abstract execute(
    request: GetEmailCyclesRPC.Request
  ): Promise<LeoRPCResult<GetEmailCyclesRPC.Response, GetEmailCyclesRPC.Errors.Errors>>;
}

export namespace GetEmailCyclesRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  
  export namespace RequestEnums {
    /* eslint-disable @typescript-eslint/no-shadow */
    export namespace SortColumn {
      export enum SortColumn {
        CREATED_AT = "CREATED_AT",
        MODIFIED_AT = "MODIFIED_AT",
      }
    
      export interface SortColumnDTO {
        case: string;
      }
    
      export const fromDTO = (dto: SortColumnDTO): SortColumn => {
        if (dto === undefined || dto === null) {
          throw new InvalidRequestSortColumnError(
            `Invalid DTO ${dto} provided for SortColumn.SortColumn`
          );
        }
        switch (dto.case) {
          case "CREATED_AT":
            return SortColumn.CREATED_AT;
          case "MODIFIED_AT":
            return SortColumn.MODIFIED_AT;
          default:
            throw new InvalidRequestSortColumnError(
              `Case ${dto.case} is not valid case of SortColumn.SortColumn`
            );
        }
      };
    
      export const toDTO = (sortColumn: SortColumn): SortColumnDTO => {
        const enumDTO = {
          case: SortColumn[sortColumn],
        };
        return enumDTO;
      };
    }
    
  }
  export interface RequestDTO {
    projectId: string;
    pageIndex: number;
    itemsPerPage: number;
    sortOrder: TypesSortOrder.TypesSortOrderDTO;
    sortColumn: RequestEnums.SortColumn.SortColumnDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetEmailCyclesRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly pageIndex: number;
    readonly itemsPerPage: number;
    readonly sortOrder: TypesSortOrder.TypesSortOrder;
    readonly sortColumn: RequestEnums.SortColumn.SortColumn;
  
    static readonly constraints = {
      "pageIndex": {
        "minValue": 0,
      },
      "itemsPerPage": {
        "minValue": 10,
      },
    }
  
    constructor(
    projectId: LeoUUID, 
    pageIndex: number, 
    itemsPerPage: number, 
    sortOrder: TypesSortOrder.TypesSortOrder, 
    sortColumn: RequestEnums.SortColumn.SortColumn
    ) {
      this.projectId = projectId;
      Request.validatePageIndex(pageIndex);
      this.pageIndex = pageIndex;
      Request.validateItemsPerPage(itemsPerPage);
      this.itemsPerPage = itemsPerPage;
      this.sortOrder = sortOrder;
      this.sortColumn = sortColumn;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const pageIndex = getInt32(dto, "pageIndex", InvalidRequestPageIndexError);
      const itemsPerPage = getInt32(dto, "itemsPerPage", InvalidRequestItemsPerPageError);
      const sortOrder = TypesSortOrder.fromDTO(getObject(dto, "sortOrder", InvalidRequestSortOrderError));
      const sortColumn = RequestEnums.SortColumn.fromDTO(getObject(dto, "sortColumn", InvalidRequestSortColumnError));
      return new Request(
        projectId,
        pageIndex,
        itemsPerPage,
        sortOrder,
        sortColumn
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          pageIndex: this.pageIndex,
          itemsPerPage: this.itemsPerPage,
          sortOrder: TypesSortOrder.toDTO(this.sortOrder),
          sortColumn: RequestEnums.SortColumn.toDTO(this.sortColumn)
      };
      return dto;
    }
    private static validatePageIndex = (pageIndex: number): void => {
      if(pageIndex < 0) {
        throw new InvalidRequestPageIndexMinValueError(
          0,
          `Attribute pageIndex has value ${pageIndex}. Min value is 0.`
        );
      }
    }
    private static validateItemsPerPage = (itemsPerPage: number): void => {
      if(itemsPerPage < 10) {
        throw new InvalidRequestItemsPerPageMinValueError(
          10,
          `Attribute itemsPerPage has value ${itemsPerPage}. Min value is 10.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetEmailCyclesRPC(projectId='${this.projectId}',pageIndex='${this.pageIndex}',itemsPerPage='${this.itemsPerPage}',sortOrder='${this.sortOrder}',sortColumn='${this.sortColumn}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    emailCycles: EmailCycleDTO[];
    totalItems: number;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetEmailCyclesResponse: boolean = true;
  
    readonly emailCycles: EmailCycle[];
    readonly totalItems: number;
  
    static readonly constraints = {
      "totalItems": {
        "minValue": 0,
      }
    }
  
    constructor(
    emailCycles: EmailCycle[], 
    totalItems: number
    ) {
      this.emailCycles = emailCycles;
      Response.validateTotalItems(totalItems);
      this.totalItems = totalItems;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const emailCycles = getList<EmailCycleDTO, EmailCycle>(
        dto,
        "emailCycles",
        EmailCycle.fromDTO,
        InvalidResponseEmailCyclesError
      );
      const totalItems = getInt32(dto, "totalItems", InvalidResponseTotalItemsError);
      return new Response(
        emailCycles,
        totalItems
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          emailCycles: this.emailCycles.map((e: EmailCycle) => { return e.toDTO() }),
          totalItems: this.totalItems
      };
      return dto;
    }
    private static validateTotalItems = (totalItems: number): void => {
      if(totalItems < 0) {
        throw new InvalidResponseTotalItemsMinValueError(
          0,
          `Attribute totalItems has value ${totalItems}. Min value is 0.`
        );
      }
    }
    
  }
  
  Response.prototype.toString = function toString(): string {
    return `GetEmailCycles(emailCycles='${this.emailCycles}',totalItems='${this.totalItems}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidPageIndex = "INVALID_PAGE_INDEX"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetEmailCyclesRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidPageIndexDTO {
      code: string;
    }
    
    export class InvalidPageIndex extends LeoRPCError<RPCError> {
      readonly isInvalidPageIndex: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidPageIndex;
        super(code);
      }
    
    
      toDTO(): InvalidPageIndexDTO {
        const dto: InvalidPageIndexDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidPageIndex.prototype.toString = function toString(): string {
      return `GetEmailCyclesRPC.INVALID_PAGE_INDEX()`;
    };
    

    export type Errors = InvalidProjectId | InvalidPageIndex;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidPageIndexDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidPageIndex:
          return new InvalidPageIndex();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetEmailCyclesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestPageIndexError extends GetEmailCyclesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPageIndexError";
  }
}

export class InvalidRequestPageIndexMinValueError extends InvalidRequestPageIndexError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPageIndexMinValueError";
  }
}

export class InvalidRequestItemsPerPageError extends GetEmailCyclesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestItemsPerPageError";
  }
}

export class InvalidRequestItemsPerPageMinValueError extends InvalidRequestItemsPerPageError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestItemsPerPageMinValueError";
  }
}

export class InvalidRequestSortOrderError extends GetEmailCyclesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortOrderError";
  }
}

export class InvalidRequestSortColumnError extends GetEmailCyclesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortColumnError";
  }
}

export class InvalidRequestSortColumnCreatedAtError extends InvalidRequestSortColumnError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortColumnCreatedAtError";
  }
}

export class InvalidRequestSortColumnModifiedAtError extends InvalidRequestSortColumnError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortColumnModifiedAtError";
  }
}

export class InvalidResponseEmailCyclesError extends GetEmailCyclesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseEmailCyclesError";
  }
}

export class InvalidResponseTotalItemsError extends GetEmailCyclesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTotalItemsError";
  }
}

export class InvalidResponseTotalItemsMinValueError extends InvalidResponseTotalItemsError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTotalItemsMinValueError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetEmailCyclesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidPageIndexError extends GetEmailCyclesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidPageIndexError";
  }
}

